"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  CheckCircle2,
  Mail,
  Phone,
  User,
  ArrowRight,
  Shield,
  GraduationCap,
  Users,
  PlayCircle,
} from "lucide-react";
import Image from "next/image";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

export default function CapturaPage() {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);

    // TODO: integrar com API de leads
    await new Promise((r) => setTimeout(r, 1200));

    setSucesso(true);
    setEnviando(false);
  }

  return (
    <main className="min-h-screen bg-[#faf7f5]">
      {/* Header */}
      <header className="w-full py-4 px-6">
        <div className="mx-auto max-w-5xl flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-violet-600" />
          <span className="text-lg font-semibold text-gray-900">eBuz</span>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-5xl px-4 pt-8 pb-16 md:pt-16 md:pb-24">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          {/* Left - Copy */}
          <div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <span className="inline-block rounded-full bg-violet-100 px-4 py-1.5 text-sm font-medium text-violet-700 mb-6">
                Webinário gratuito
              </span>
            </motion.div>

            <motion.h1
              className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold leading-tight text-gray-900 mb-5"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              Descubra Como Transformar Seu Conhecimento em um Produto
              Educacional que Vende Sem Você Precisar Estar Presente
            </motion.h1>

            <motion.p
              className="text-lg text-gray-600 mb-8"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              Webinário gratuito de 45 minutos com Jonathan Machado
            </motion.p>

            {/* Bullet points */}
            <motion.div
              className="space-y-4 mb-8"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              {[
                "Por que 90% dos experts fracassam ao criar produtos educacionais (e o erro que ninguém fala)",
                "O framework de 5 nos usado por 12 pesquisadores em 50 anos de ciência",
                "Como montar sua esteira de produtos sem depender de presença física",
              ].map((item, i) => (
                <div key={i} className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-violet-600 mt-0.5 shrink-0" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </motion.div>

            {/* Social proof */}
            <motion.div
              className="flex flex-wrap gap-6 text-sm text-gray-500"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
            >
              <div className="flex items-center gap-1.5">
                <GraduationCap className="h-4 w-4" />
                <span>3 cursos publicados</span>
              </div>
              <div className="flex items-center gap-1.5">
                <PlayCircle className="h-4 w-4" />
                <span>141 aulas</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                <span>125+ alunos</span>
              </div>
            </motion.div>

            {/* Author photo */}
            <motion.div
              className="flex items-center gap-4 mt-8"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={5}
            >
              <Image
                src="/images/jonathan-machado.jpg"
                alt="Jonathan Machado"
                width={64}
                height={64}
                className="rounded-full border-2 border-violet-200"
              />
              <div>
                <p className="font-semibold text-gray-900">Jonathan Machado</p>
                <p className="text-sm text-gray-500">Criador da Metodologia APE</p>
              </div>
            </motion.div>

            {/* Book mockup - social proof */}
            <motion.div
              className="mt-6 flex items-center gap-4 rounded-xl bg-violet-50 p-4"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={6}
            >
              <Image
                src="/images/mockup-livro-ape.webp"
                alt="Livro APE - Arquitetura de Produtos Educacionais"
                width={80}
                height={107}
                className="rounded-lg shadow-md"
              />
              <div>
                <p className="text-sm font-medium text-violet-700">Baseado no livro</p>
                <p className="text-sm text-gray-600">APE - Arquitetura de Produtos Educacionais</p>
              </div>
            </motion.div>
          </div>

          {/* Right - Form */}
          <motion.div
            className="rounded-2xl bg-white p-6 md:p-8 shadow-lg border border-gray-100"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            {sucesso ? (
              <div className="text-center py-8">
                <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Pronto! Você está dentro.
                </h2>
                <p className="text-gray-600 mb-6">
                  Verifique seu email para acessar o webinário agora mesmo.
                </p>
                <a
                  href="/webinário"
                  className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-3 text-white font-semibold hover:bg-violet-700 transition-colors"
                >
                  Assistir Agora <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  Assista ao webinário gratuito
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  Preencha seus dados para liberar o acesso imediato.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="nome"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Seu nome
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        id="nome"
                        type="text"
                        required
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        placeholder="Como você gostaria de ser chamado?"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Seu melhor email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seuemail@exemplo.com"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="telefone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      WhatsApp{" "}
                      <span className="text-gray-400 font-normal">
                        (opcional)
                      </span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        id="telefone"
                        type="tel"
                        value={telefone}
                        onChange={(e) => setTelefone(e.target.value)}
                        placeholder="(11) 99999-9999"
                        className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={enviando}
                    className="w-full rounded-xl bg-violet-600 py-3.5 text-white font-semibold text-lg hover:bg-violet-700 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {enviando ? (
                      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    ) : (
                      <>
                        Quero Assistir Agora
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </form>

                <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                  <Shield className="h-3.5 w-3.5" />
                  Seu email está seguro. Não enviamos spam.
                </p>
              </>
            )}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
